import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import SuggestionCard from "../../../components/suggestions/SuggestionCard";
import { ISuggestion, useSuggestions } from "../../../context/SuggestionsContext";
import { NotificationFeedback } from "./notificationsUi";

/**
 * Single notification view for the embedded mobile app. The notification is
 * identified by the `id` query string parameter (its numeric Id, e.g.
 * #/embedded-app/notifications/detail?id=1234); `ID` is accepted as an alias.
 *
 * The suggestions API has no dedicated by-id endpoint, so the id is resolved via
 * the user-scoped list endpoint with an OData $filter (see getSuggestionById).
 */
const NotificationDetail = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getSuggestionById, suggestionSubmitting } = useSuggestions();

    const [suggestion, setSuggestion] = useState<ISuggestion | null>(null);
    const [loading, setLoading] = useState(true);
    const [prevSubmitting, setPrevSubmitting] = useState(false);

    const rawId = searchParams.get('id') ?? searchParams.get('ID');
    const id = rawId !== null && rawId.trim() !== '' && !Number.isNaN(Number(rawId))
        ? Number(rawId)
        : null;

    /** Back to the list, keeping host supplied params (darkTheme, ...). */
    const backToList = () => {
        const params = new URLSearchParams(searchParams);
        params.delete('id');
        params.delete('ID');
        const query = params.toString();
        navigate(`/embedded-app/notifications${query ? `?${query}` : ''}`);
    };

    const load = useCallback(async () => {
        if (id === null) { setLoading(false); return; }
        setLoading(true);
        const result = await getSuggestionById(id);
        setSuggestion(result);
        setLoading(false);
    }, [id, getSuggestionById]);

    useEffect(() => { load(); }, [load]);

    // Re-fetch once an execute-proc action finishes (submitting true -> false),
    // so a now-dismissed notification falls through to the "not found" state.
    useEffect(() => {
        if (prevSubmitting && !suggestionSubmitting) load();
        setPrevSubmitting(suggestionSubmitting);
    }, [suggestionSubmitting, prevSubmitting, load]);

    const header = (
        <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={backToList} aria-label="Back to notifications">
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-lg font-semibold">Notification</h1>
        </div>
    );

    if (id === null) {
        return (
            <div className="mx-auto flex w-full max-w-xl flex-col gap-3 p-3">
                {header}
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Missing notification id</AlertTitle>
                    <AlertDescription>Add an <code>id</code> parameter to the url, e.g. <code>?id=1234</code>.</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="mx-auto flex w-full max-w-xl flex-col gap-3 p-3 pb-8">
            {header}

            {loading && (
                <Card>
                    <CardContent className="flex flex-col gap-4 p-4">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-5 w-40" />
                                <Skeleton className="h-3 w-28" />
                            </div>
                        </div>
                        <Skeleton className="h-16 w-full" />
                    </CardContent>
                </Card>
            )}

            {!loading && !suggestion && (
                <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Notification not found</AlertTitle>
                    <AlertDescription>
                        This notification is no longer available. It may have been completed or dismissed.
                    </AlertDescription>
                </Alert>
            )}

            {!loading && suggestion && (
                <SuggestionCard
                    suggestion={suggestion}
                    size="full"
                    refreshParent={load}
                    showDocuSignMessage={true}
                    adminMode={false}
                />
            )}

            <NotificationFeedback />
        </div>
    );
};

export default NotificationDetail;
